import useSWRMutation from "swr/mutation";
import { environment } from "../config/environment";
import { EmailResponseModel } from "../models/EmailModels";
import { useSWRFetcher } from "./useSWRFetcher";
import { useContext } from "react";
import { ToastContext } from "../context/ToastContext";
import { useOutlook } from "./useOutlook";
import { isMailboxRequirementSupported } from "../taskpane";

type AttachmentItem = {
  id: string;
  name: string;
  type: string;
};

export const useDraftEmail = () => {
  const fetcher = useSWRFetcher();
  const { showToast } = useContext(ToastContext);
  const { isInReadMode } = useOutlook();

  const dataType = isInReadMode && !isMailboxRequirementSupported("1.15") ? "Url" : "Base64";

  const {
    trigger: draftReply,
    isMutating: isDraftReplyLoading,
    error: draftReplyError,
  } = useSWRMutation(
    `${environment.apiUrl}/api/emails/drafts/reply`,
    (
      url: string,
      {
        arg,
      }: {
        arg: {
          userPrompt: string;
          receivedEmailContent: string;
          styleId?: string;
          language?: string;
          attachments?: AttachmentItem[];
        };
      }
    ) =>
      fetcher<EmailResponseModel>(url, {
        method: "POST",
        body: JSON.stringify({
          userPrompt: arg.userPrompt,
          recievedEmailContent: arg.receivedEmailContent,
          styleId: arg.styleId,
          language: arg.language,
          attachments: {
            attachmentRequests: arg.attachments,
            dataType,
          },
        }),
      }),
    {
      onError: () => {
        showToast("Failed to draft the reply email, please try again later", "error");
      },
    }
  );

  const {
    trigger: updateDraftReply,
    isMutating: isUpdateDraftReplyLoading,
    error: updateDraftReplyError,
  } = useSWRMutation(
    `${environment.apiUrl}/api/emails/drafts/reply/update`,
    (
      url: string,
      {
        arg,
      }: {
        arg: {
          userPrompt: string;
          currentDraft: string;
          receivedEmailContent: string;
          styleId?: string;
          language?: string;
          attachments?: AttachmentItem[];
        };
      }
    ) =>
      fetcher<EmailResponseModel>(url, {
        method: "POST",
        body: JSON.stringify({
          userPrompt: arg.userPrompt,
          currentDraft: arg.currentDraft,
          recievedEmailContent: arg.receivedEmailContent,
          styleId: arg.styleId,
          language: arg.language,
          attachments: {
            attachmentRequests: arg.attachments,
            dataType,
          },
        }),
      }),
    {
      onError: () => {
        showToast("Failed to update the drafted email, please try again later", "error");
      },
    }
  );

  const {
    trigger: draftNewReply,
    isMutating: isDraftNewReplyLoading,
    error: draftNewReplyError,
  } = useSWRMutation(
    `${environment.apiUrl}/api/emails/drafts/new`,
    (
      url: string,
      { arg }: { arg: { userPrompt: string; styleId?: string; language?: string; attachments?: AttachmentItem[] } }
    ) =>
      fetcher<EmailResponseModel>(url, {
        method: "POST",
        body: JSON.stringify({
          userPrompt: arg.userPrompt,
          styleId: arg.styleId,
          language: arg.language,
          attachments: {
            attachmentRequests: arg.attachments,
            dataType,
          },
        }),
      }),
    {
      onError: () => {
        showToast("Failed to draft the email, please try again later", "error");
      },
    }
  );

  const {
    trigger: updateDraftNewReply,
    isMutating: isUpdateDraftNewReplyLoading,
    error: updateDraftNewReplyError,
  } = useSWRMutation(
    `${environment.apiUrl}/api/emails/drafts/new/update`,
    (
      url: string,
      {
        arg,
      }: {
        arg: {
          userPrompt: string;
          currentDraft: string;
          styleId?: string;
          language?: string;
          attachments?: AttachmentItem[];
        };
      }
    ) =>
      fetcher<EmailResponseModel>(url, {
        method: "POST",
        body: JSON.stringify({
          userPrompt: arg.userPrompt,
          currentDraft: arg.currentDraft,
          styleId: arg.styleId,
          language: arg.language,
          attachments: {
            attachmentRequests: arg.attachments,
            dataType,
          },
        }),
      }),
    {
      onError: () => {
        showToast("Failed to update the drafted email, please try again later", "error");
      },
    }
  );

  return {
    draftReply,
    isDraftReplyLoading,
    draftReplyError,
    updateDraftReply,
    isUpdateDraftReplyLoading,
    updateDraftReplyError,
    draftNewReply,
    isDraftNewReplyLoading,
    draftNewReplyError,
    updateDraftNewReply,
    isUpdateDraftNewReplyLoading,
    updateDraftNewReplyError,
  };
};
